import type { BpaDocumentDto } from "../apis/dischargeService";
import type { HmoQueryResponse } from "../apis/hmoQueryService";
import { CLAIM_DOC_TYPES, DISCHARGE_DOC_TYPES } from "../components/patient/patient-details/stageDocumentUtils";

export type QueryUploadDocument = {
  documentType: string;
  documentLabelType: string;
  documentName: string;
  documentUrl: string;
  viewUrl?: string;
  file?: File;
  id?: number;
};

const isBrowserViewableUrl = (url?: string | null): boolean => {
  const value = String(url || "").trim();
  if (!value) return false;
  return /^https?:\/\//i.test(value) || value.startsWith("blob:");
};

/** Reject signed URLs that incorrectly embed local file:// paths as GCS keys. */
export const isUsableQueryDocumentViewUrl = (url?: string | null): boolean => {
  const value = String(url || "").trim();
  if (!isBrowserViewableUrl(value)) return false;
  if (/file%3A|file:\/\//i.test(value)) return false;
  // API download/view routes require Authorization; iframe/img cannot send the bearer token.
  if (/\/documents\/\d+\/(download|view-url)/i.test(value)) return false;
  return true;
};

/** Normalize query API documents for UI preview and required-doc matching. */
export const normalizeQueryReturnedDocuments = (
  query: Pick<
    HmoQueryResponse,
    "returnedDocumentUrl" | "returnedDocuments" | "queryRequiredDocuments"
  >,
): QueryUploadDocument[] => {
  let documents: QueryUploadDocument[] = [];

  if (Array.isArray(query.returnedDocumentUrl) && query.returnedDocumentUrl.length > 0) {
    documents = query.returnedDocumentUrl.map((doc: any) => ({
      id: doc.id,
      documentType: resolveQueryDocumentMasterName(
        doc.documentType || doc.documentTypeName || doc.documentLabelType || "OTHER",
      ),
      documentLabelType:
        doc.documentLabelType ||
        doc.documentTypeName ||
        doc.documentType ||
        "OTHER",
      documentName: doc.documentName || "",
      documentUrl: doc.documentUrl || "",
      viewUrl: doc.viewUrl,
    }));
  } else if (query.returnedDocuments?.length) {
    documents = query.returnedDocuments.map((doc) => ({
      id: doc.id,
      documentType: resolveQueryDocumentMasterName(
        doc.documentTypeName || doc.documentLabelType || "OTHER",
      ),
      documentLabelType: doc.documentLabelType || doc.documentTypeName || "OTHER",
      documentName: doc.documentName || "",
      documentUrl: doc.documentUrl || "",
      viewUrl: doc.viewUrl,
    }));
  } else if (typeof query.returnedDocumentUrl === "string" && query.returnedDocumentUrl.trim()) {
    documents = [
      {
        documentType: "OTHER",
        documentLabelType: "OTHER",
        documentName: "",
        documentUrl: query.returnedDocumentUrl.trim(),
      },
    ];
  }

  return applyRequiredDocumentLabels(documents, query.queryRequiredDocuments);
};

const applyRequiredDocumentLabels = (
  documents: QueryUploadDocument[],
  requiredDocuments?: HmoQueryResponse["queryRequiredDocuments"],
): QueryUploadDocument[] => {
  if (!documents.length || !requiredDocuments?.length) {
    return documents;
  }

  if (documents.length === 1 && requiredDocuments.length === 1) {
    const requiredName = requiredDocuments[0]?.name;
    if (requiredName && !queryUploadMatchesLabels(documents[0], requiredName)) {
      return [
        {
          ...documents[0],
          documentLabelType: requiredName,
          documentType: resolveQueryDocumentMasterName(requiredName),
        },
      ];
    }
  }

  return documents.map((doc, index) => {
    const required = requiredDocuments.find((item) =>
      queryUploadMatchesLabels(doc, item?.name),
    );
    if (required?.name) {
      return { ...doc, documentLabelType: required.name };
    }
    const byIndex = requiredDocuments[index]?.name;
    if (
      byIndex &&
      documents.length === requiredDocuments.length &&
      !queryUploadMatchesLabels(doc, byIndex)
    ) {
      return {
        ...doc,
        documentLabelType: byIndex,
        documentType: resolveQueryDocumentMasterName(byIndex),
      };
    }
    return doc;
  });
};

const MASTER_NAME_BY_CODE: Record<string, string> = {
  DISCHARGE_SUMMARY: "DISCHARGE SUMMARY",
  GEO_TAG_PHOTO: "GEO TAG PHOTO",
  FEEDBACK_FORM: "FEEDBACK FORM",
  CONSULTATION_NOTE: "CONSULTATION NOTE",
  HOSPITAL_BILL: "HOSPITAL BILL",
  INVESTIGATION_REPORT: "INVESTIGATION REPORT",
  OTHER: "OTHER",
};

/** Map UI / stage label to document master name expected by query reply API. */
export const resolveQueryDocumentMasterName = (labelOrType: string): string => {
  const raw = String(labelOrType || "").trim();
  if (!raw) return "OTHER";

  const upper = raw.toUpperCase();
  if (MASTER_NAME_BY_CODE[upper]) {
    return MASTER_NAME_BY_CODE[upper];
  }
  if (
    upper === "CLINICAL NOTES" ||
    upper === "COST BREAKUP" ||
    upper === "CLINICAL_NOTES" ||
    upper === "COST_BREAKUP"
  ) {
    return "OTHER";
  }

  const fromClaim = CLAIM_DOC_TYPES[raw] || CLAIM_DOC_TYPES[raw.toLowerCase()];
  if (fromClaim && MASTER_NAME_BY_CODE[fromClaim]) {
    return MASTER_NAME_BY_CODE[fromClaim];
  }

  const fromDischarge = DISCHARGE_DOC_TYPES[raw];
  if (fromDischarge && MASTER_NAME_BY_CODE[fromDischarge]) {
    return MASTER_NAME_BY_CODE[fromDischarge];
  }

  return upper;
};

export const findStageDocumentById = (
  documents: BpaDocumentDto[] | undefined,
  documentId?: number | null,
): BpaDocumentDto | undefined => {
  if (!documentId || !documents?.length) return undefined;
  return documents.find((doc) => doc.id === documentId);
};

export const buildOldDocumentFromQuery = (
  query: Pick<HmoQueryResponse, "oldDocumentId" | "returnedDocumentUrl">,
  stageDocuments?: any[],
) => {
  const stageDoc = findStageDocumentById(stageDocuments, query.oldDocumentId);
  if (stageDoc) {
    const label = stageDoc.documentTypeName || "Document";
    return {
      id: stageDoc.id,
      documentLabelType: label,
      documentType: resolveQueryDocumentMasterName(label),
      documentName: stageDoc.documentName,
      deficiencyCheckStatus: stageDoc.deficiencyCheckStatus,
      remarks: stageDoc.remarks,
    };
  }

  if (!query.oldDocumentId) return undefined;

  return {
    id: query.oldDocumentId,
    documentLabelType: "Document",
    documentType: "OTHER",
    documentUrl: query.returnedDocumentUrl,
  };
};

export const areAllRequiredDocumentsUploaded = (
  uploads: QueryUploadDocument[],
  requiredDocuments?: { name: string }[] | null,
): boolean => {
  if (!requiredDocuments?.length) return true;
  return requiredDocuments.every((req) =>
    uploads.some(
      (doc) =>
        (Boolean(doc.id) || Boolean(doc.file)) &&
        queryUploadMatchesLabels(doc, req.name),
    ),
  );
};

export const isDeficiencyDocumentUploaded = (
  uploads: QueryUploadDocument[],
  oldDocumentLabel?: string | null,
): boolean => {
  const hasUploaded = (doc: QueryUploadDocument) =>
    Boolean(doc.id) || Boolean(doc.file);
  if (!oldDocumentLabel) return uploads.some(hasUploaded);
  return uploads.some(
    (doc) => hasUploaded(doc) && queryUploadMatchesLabels(doc, oldDocumentLabel),
  );
};

export const mergeQueryUpload = (
  prev: QueryUploadDocument[],
  entry: QueryUploadDocument,
): QueryUploadDocument[] => {
  const existing = prev.find((doc) =>
    queryUploadMatchesLabels(doc, entry.documentLabelType, entry.documentType),
  );
  if (existing?.documentUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(existing.documentUrl);
  }
  return [
    ...prev.filter(
      (doc) => !queryUploadMatchesLabels(doc, entry.documentLabelType, entry.documentType),
    ),
    entry,
  ];
};

const normalizeQueryDocKey = (value?: string | null) =>
  String(value || "").trim().toUpperCase();

export const queryUploadMatchesLabels = (
  doc: Pick<QueryUploadDocument, "documentLabelType" | "documentType">,
  label?: string | null,
  documentType?: string | null,
): boolean => {
  const docKeys = new Set(
    [doc.documentLabelType, doc.documentType]
      .map(normalizeQueryDocKey)
      .filter(Boolean),
  );
  const targetKeys = [label, documentType]
    .map(normalizeQueryDocKey)
    .filter(Boolean);
  if (targetKeys.some((key) => docKeys.has(key))) {
    return true;
  }

  if (label) {
    const masterName = normalizeQueryDocKey(resolveQueryDocumentMasterName(label));
    if (masterName && docKeys.has(masterName)) {
      return true;
    }
  }

  return false;
};

export const findReturnedDocumentsForRequiredName = (
  documents: QueryUploadDocument[] | undefined | null,
  requiredName?: string | null,
  requiredDocuments?: Array<{ name?: string | null }> | null,
): QueryUploadDocument[] => {
  if (!documents?.length || !requiredName) {
    return [];
  }

  const directMatches = documents.filter((doc) =>
    queryUploadMatchesLabels(doc, requiredName),
  );
  if (directMatches.length) {
    return directMatches;
  }

  if (documents.length === 1 && requiredDocuments?.length === 1) {
    return documents;
  }

  return [];
};

export const findQueryDocumentTabKey = (
  documents: Array<
    Pick<QueryUploadDocument, "documentLabelType" | "documentType">
  >,
  label?: string | null,
): string | undefined => {
  if (!label || !documents.length) return undefined;
  const match = documents.find((doc) => queryUploadMatchesLabels(doc, label));
  return match?.documentLabelType || match?.documentType || label;
};

export const queryUploadMatchesRow = (
  doc: QueryUploadDocument,
  row: { label: string; documentType: string },
): boolean => queryUploadMatchesLabels(doc, row.label, row.documentType);

/** Local preview URL for a staged or just-uploaded query document. */
export const resolveQueryUploadPreviewUrl = (
  upload: Pick<QueryUploadDocument, "documentUrl" | "viewUrl" | "file">,
): string | null => {
  if (upload.viewUrl && isUsableQueryDocumentViewUrl(upload.viewUrl)) {
    return upload.viewUrl;
  }
  if (upload.documentUrl && isUsableQueryDocumentViewUrl(upload.documentUrl)) {
    return upload.documentUrl;
  }
  if (upload.file) {
    return URL.createObjectURL(upload.file);
  }
  return null;
};

export const createQueryUploadEntry = (
  file: File,
  documentType: string,
  documentLabel: string,
): QueryUploadDocument => ({
  documentType: resolveQueryDocumentMasterName(documentType || documentLabel),
  documentLabelType: documentLabel,
  documentName: file.name,
  documentUrl: URL.createObjectURL(file),
  file,
});

export const toQueryReplyDocuments = (uploads: QueryUploadDocument[]) =>
  uploads.map((doc) => ({
    documentType: doc.documentType,
    documentName: doc.documentName,
    documentUrl: doc.documentUrl,
    id: doc.id ?? 0,
    file: doc.file,
    referenceType: null,
    documentLabelType: doc.documentLabelType ?? null,
    deficiencyCheckStatus: null,
    remarks: null,
  }));
